import { useCallback, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  Checkbox,
  Button,
  InlineStack,
  Box,
  Icon,
  SkeletonThumbnail,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  EditIcon,
  PlayIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { AddMediaModal } from "@shared/ui";
import { GenerativeTextField } from "@shared/ui";
import {
  Event,
  GallerySection,    
  MultilingualText,
  MediaItem,
  ImageDetailType,
  Instructor,
  Venue,
} from "@features/events/types";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { MediaEntityType } from "@shared/ui";
import { getLocalizedText } from "@shared/lib";

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^\s&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

interface MediaItemProps {
  item: MediaItem;
  onRemove: (itemId: string) => void;
  onEdit: (item: MediaItem) => void;
}

const MediaItemComponent: React.FC<MediaItemProps> = ({
  item,
  onRemove,
  onEdit,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`Edit ${getLocalizedText(item.name) || "media item"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(item.name) || "media item"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            {item.type === "vid" ? (
              item.source && getYouTubeThumbnail(item.source as string) ? (
                <img
                  src={getYouTubeThumbnail(item.source as string)!}
                  alt={getLocalizedText(item.name)}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Icon source={PlayIcon} />
              )
            ) : item.source ? (
              <img
                src={(item.source as ImageDetailType[])[0]?.src || ""}
                alt={getLocalizedText(item.name)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <SkeletonThumbnail size="small" />
            )}

            <BlockStack gap="100">
              <Text as="span" variant="bodyMd">
                {getLocalizedText(item.name)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(item)}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(item.name.bg)}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

interface WhyAttendCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
  eventInstructors?: Partial<Instructor>[];
  eventVenues?: Partial<Venue>[];
}

export default function WhyAttendCard({
  eventData,
  onDataChange,
  fieldErrors = {},
  eventInstructors,
  eventVenues,
}: WhyAttendCardProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  // Extract current values from eventData.whyAttend
  const whyAttend = useMemo(() => eventData.whyAttend, [eventData.whyAttend]);
  const enableWhyAttend = useMemo(() => Boolean(whyAttend), [whyAttend]);
  const sectionTitle = useMemo(() => whyAttend?.title, [whyAttend?.title]);
  const sectionDesc = useMemo(
    () => whyAttend?.description,
    [whyAttend?.description]
  );

  // Convert API format to MediaItem format for display
  const mediaItems: MediaItem[] = useMemo(
    () =>
      whyAttend?.media?.map((media) => ({
        type: media.type === "vid" ? "vid" : "img",
        name: { bg: media.name.bg, en: media.name.en },
        source: media.source || "",
      })) || [],
    [whyAttend?.media]
  );

  const updateWhyAttend = useCallback(
    (
      updates: Partial<{
        enabled: boolean;
        title: MultilingualText;
        description: MultilingualText;
        media: MediaItem[];
      }>
    ) => {
      if (updates.enabled === false) {
        // Remove whyAttend when disabled
        onDataChange({ whyAttend: undefined });
        return;
      }

      // Convert MediaItem format to API format
      const whyAttendData: GallerySection = {
        title: updates.title ?? sectionTitle ?? {},
        description: updates.description ?? sectionDesc ?? {},
        media: (updates.media ?? mediaItems).map(
          (item): MediaItem => ({
            type: item.type === "vid" ? "vid" : "img",
            name: { bg: item.name.bg, en: item.name.en },
            source: item.source || "",
          })
        ),
      };

      onDataChange({ whyAttend: whyAttendData });
    },
    [onDataChange, sectionTitle, sectionDesc, mediaItems]
  );

  const handleToggleWhyAttend = (checked: boolean) => {
    updateWhyAttend({ enabled: checked });
  };

  const renderMediaItem = (
    item: MediaItem
  ) => (
    <MediaItemComponent
      item={item}
      onRemove={handleRemoveMedia}
      onEdit={handleEditClick}
    />
  );

  const handleAddMedia = (mediaArray: MediaItem[]) => {
    updateWhyAttend({ media: [...mediaItems, ...mediaArray] });
  };

  const handleEditMedia = (updatedMedia: MediaItem) => {
    const updatedItems = mediaItems.map((item) =>
      item.name.bg === updatedMedia.name.bg ? updatedMedia : item
    );
    updateWhyAttend({ media: updatedItems });
  };

  const handleRemoveMedia = (id: string) => {
    const updatedItems = mediaItems.filter((item) => item.name.bg !== id);
    updateWhyAttend({ media: updatedItems });
  };

  const handleSectionTitleChange = (value: Record<string, string>) => {
    updateWhyAttend({
      title: value,
    });
  };

  const handleSectionDescChange = (value: Record<string, string>) => {
    updateWhyAttend({
      description: value,
    });
  };

  const handleEditClick = (item: MediaItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("eventDetails.highlights.title")}
        </Text>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("eventDetails.highlights.description")}
        </Text>

        <Checkbox
          label={t("eventDetails.highlights.enableCheckbox")}
          checked={enableWhyAttend}
          onChange={handleToggleWhyAttend}
        />

        {enableWhyAttend && (
          <BlockStack gap="400">
            <GenerativeTextField
              id="section-title"
              placementId="event.event.section.title"
              label={t("eventDetails.highlights.sectionTitle")}
              value={sectionTitle ?? {}}
              onChange={handleSectionTitleChange}
              type="textField"
              error={fieldErrors["whyAttend.title"]}
            />

            <BlockStack gap="300">
              <GenerativeTextField
                id="section-description"
                placementId="event.event.section.description"
                label={t("eventDetails.highlights.sectionDescription")}
                value={sectionDesc ?? {}}
                onChange={handleSectionDescChange}
                type="textArea"
              />
            </BlockStack>

            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                {t("eventDetails.highlights.mediaHighlights")}
              </Text>

              {mediaItems.length === 0 ? (
                <Box padding="600" borderRadius="200">
                  <InlineStack align="center">
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {t("eventDetails.highlights.noMediaAdded")}
                    </Text>
                  </InlineStack>
                </Box>
              ) : (
                <Box
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  background="bg-surface"
                >
                  <SortableList
                    items={mediaItems}
                    onChange={(reorderedMedia) => {
                      updateWhyAttend({ media: reorderedMedia });
                    }}
                    idForItem={(item) =>
                      `${item?.name?.bg}-${Object.values(item?.name)[0]}`
                    }
                    renderItem={(item) => renderMediaItem(item)}
                    showDividers={true}
                  />
                </Box>
              )}

              <InlineStack align="center">
                <Button
                  icon={PlusIcon}
                  variant="primary"
                  onClick={() => setIsModalOpen(true)}
                >
                  {t("eventDetails.highlights.addMedia")}
                </Button>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        )}
      </BlockStack>

      <AddMediaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddMedia={handleAddMedia}
        onEditMedia={handleEditMedia}
        editingItem={editingItem}
        hcc={hcc}
        entityType={MediaEntityType.EVENT}
        entityId={eventData.id}
        eventData={eventData}
        eventInstructors={eventInstructors}
        eventVenues={eventVenues}
      />
    </Card>
  );
}
